import { API, Auth } from 'aws-amplify';

// Name of the API configured in aws-config.ts
const API_NAME = 'CustomerFeedbackAPI';

// Utility function to handle API responses
const handleApiResponse = (response: any) => {
  // Check if we have a valid response object
  if (response && response.data !== undefined) {
    return response;
  }

  // If it's a direct response from API.get/post (not wrapped)
  return { data: response };
};

// Configuration API endpoints
export const configApi = {
  getAll: async () => {
    try {
      const response = await API.get(API_NAME, '/config', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error getting configuration:', error);
      throw error;
    }
  },
  update: async (config: any) => {
    try {
      const response = await API.put(API_NAME, '/config', { body: config });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error updating configuration:', error);
      throw error;
    }
  },
  testConnection: async () => {
    try {
      const response = await API.post(API_NAME, '/config/test-connection', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error testing connections:', error);
      throw error;
    }
  }
};

// Event Testing API endpoints
export const eventApi = {
  triggerTest: async (eventData: any) => {
    try {
      const response = await API.post(API_NAME, '/events/test', { body: eventData });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error triggering test event:', error);
      throw error;
    }
  },
  testSlack: async (eventData: any) => {
    try {
      const response = await API.post(API_NAME, '/events/test-slack', { body: eventData });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error testing Slack notification:', error);
      throw error;
    }
  },
  getStatistics: async () => {
    try {
      const response = await API.get(API_NAME, '/events/statistics', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error getting event statistics:', error);
      throw error;
    }
  },
  runProcessor: async () => {
    try {
      const response = await API.post(API_NAME, '/events/process', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error running event processor:', error);
      throw error;
    }
  },
  runEscalation: async () => {
    try {
      const response = await API.post(API_NAME, '/events/escalate', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error running event escalation:', error);
      throw error;
    }
  }
};

// Notification API endpoints
export const notificationsApi = {
  getAll: async (params?: {
    page?: number;
    perPage?: number;
    status?: string[];
    eventType?: string[];
    customerName?: string;
    accountManager?: string;
    dateFrom?: string;
    dateTo?: string;
    sortField?: string;
    sortDirection?: 'ASC' | 'DESC';
  }) => {
    try {
      // Convert arrays to comma-separated strings for query parameters
      const queryParams: any = {};
      if (params) {
        Object.keys(params).forEach(key => {
          const value = (params as any)[key];
          if (Array.isArray(value)) {
            queryParams[key] = value.join(',');
          } else if (value !== undefined && value !== null) {
            queryParams[key] = value;
          }
        });
      }
      
      const response = await API.get(API_NAME, '/notifications', { 
        queryStringParameters: queryParams 
      });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  },
  getById: async (id: string | number) => {
    try {
      const response = await API.get(API_NAME, `/notifications/${id}`, {});
      return handleApiResponse(response);
    } catch (error) {
      console.error(`Error getting notification ${id}:`, error);
      throw error;
    }
  },
  resend: async (id: string | number) => {
    try {
      const response = await API.post(API_NAME, `/notifications/${id}/resend`, {});
      return handleApiResponse(response);
    } catch (error) {
      console.error(`Error resending notification ${id}:`, error);
      throw error;
    }
  },
  escalate: async (id: string | number) => {
    try {
      const response = await API.post(API_NAME, `/notifications/${id}/escalate`, {});
      return handleApiResponse(response);
    } catch (error) {
      console.error(`Error escalating notification ${id}:`, error);
      throw error;
    }
  },
  bulkResend: async (notificationIds: number[]) => {
    try {
      const response = await API.post(API_NAME, '/notifications/bulk/resend', {
        body: { notificationIds }
      });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error bulk resending notifications:', error);
      throw error;
    }
  },
  bulkEscalate: async (notificationIds: number[]) => {
    try {
      const response = await API.post(API_NAME, '/notifications/bulk/escalate', {
        body: { notificationIds }
      });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error bulk escalating notifications:', error);
      throw error;
    }
  },
  getStatistics: async () => {
    try {
      const response = await API.get(API_NAME, '/notifications/statistics', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error getting notification statistics:', error);
      throw error;
    }
  }
};

// Templates API endpoints
export const templatesApi = {
  getAll: async () => {
    try {
      const response = await API.get(API_NAME, '/templates', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error getting templates:', error);
      throw error;
    }
  },
  getByName: async (name: string) => {
    try {
      const response = await API.get(API_NAME, `/templates/${name}`, {});
      return handleApiResponse(response);
    } catch (error) {
      console.error(`Error getting template ${name}:`, error);
      throw error;
    }
  },
  preview: async (name: string, data: any) => {
    try {
      const response = await API.post(API_NAME, `/templates/${name}/preview`, { body: data });
      return handleApiResponse(response);
    } catch (error) {
      console.error(`Error previewing template ${name}:`, error);
      throw error;
    }
  }
};

// Dashboard API endpoints
export const dashboardApi = {
  getSummary: async () => {
    try {
      const response = await API.get(API_NAME, '/dashboard/summary', {});
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error getting dashboard summary:', error);
      throw error;
    }
  },
  getRecentActivity: async (limit?: number) => {
    try {
      const response = await API.get(API_NAME, '/dashboard/recent-activity', { 
        queryStringParameters: limit ? { limit } : {} 
      });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error getting recent activity:', error);
      throw error;
    }
  }
};

// Authentication helper function to check if user is authenticated
export const authHelper = {
  isAuthenticated: async (): Promise<boolean> => {
    try {
      await Auth.currentSession();
      return true;
    } catch (error) {
      return false;
    }
  },
  getToken: async (): Promise<string | null> => {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      return null;
    }
  },
  isAdmin: async (): Promise<boolean> => {
    try {
      const session = await Auth.currentSession();
      const groups = session.getIdToken().payload['cognito:groups'] || [];
      return Array.isArray(groups) && groups.includes('Admins');
    } catch (error) {
      return false;
    }
  }
};

export default {
  configApi,
  eventApi,
  notificationsApi,
  templatesApi,
  dashboardApi,
  authHelper
};
